import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Card,
  CardContent,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  Pets,
  LocationOn,
  Male,
  Female,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";
import { useDashboard } from "../../../contexts/DashboardContext";
import { getAnimalPassport } from "../../../services/cattle";

export interface AnimalData {
  _key?: string;
  _id?: string;
  _rev?: string;
  farm_id?: string;
  current_farm_id?: string;
  name?: string;
  cattle_id?: {
    tag?: string;
    country_code?: string;
    manufacturer_code?: number;
    unique_id?: string;
  };
  breed_code?: string;
  gender?: string;
  birth_date?: string;
  birth_weight_kg?: number;
  current_paddock_id?: string;
  current_status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AnimalListData {
  data: AnimalData[];
  message?: string;
  count?: number;
}

interface AnimalListViewProps {
  animalListData: AnimalListData;
}

const statusChipColor = (
  status?: string
): "default" | "success" | "warning" | "error" => {
  if (!status) return "default";
  const s = status.toLowerCase();
  if (
    s.includes("active") ||
    s.includes("ativo") ||
    s.includes("saud") ||
    s.includes("ok")
  )
    return "success";
  if (s.includes("pend") || s.includes("aten") || s.includes("warning"))
    return "warning";
  if (
    s.includes("erro") ||
    s.includes("doent") ||
    s.includes("inactive") ||
    s.includes("inativo")
  )
    return "error";
  return "default";
};

const getGenderIcon = (gender?: string) => {
  if (!gender) return null;
  const g = gender.toLowerCase();
  if (g.includes("macho") || g.includes("male")) {
    return <Male sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.primary[600] }} />;
  }
  if (g.includes("fêmea") || g.includes("femea") || g.includes("female")) {
    return <Female sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.secondary[600] }} />;
  }
  return null;
};

const calculateAge = (birthDate: string | undefined, t: any): string => {
  if (!birthDate) return t('not_defined');

  try {
    const birth = new Date(birthDate);
    const today = new Date();

    let months = (today.getFullYear() - birth.getFullYear()) * 12 +
      (today.getMonth() - birth.getMonth());

    if (today.getDate() < birth.getDate()) {
      months--;
    }

    if (months < 0) months = 0;

    return months === 1
      ? t('time_units.months_one', { count: months })
      : t('time_units.months_other', { count: months });
  } catch {
    return t('not_defined');
  }
};

const formatBreed = (breedCode: string | undefined, t: any): string => {
  if (!breedCode) return t('animal.not_defined');
  return breedCode.charAt(0).toUpperCase() + breedCode.slice(1).toLowerCase();
};

export const AnimalListView: React.FC<AnimalListViewProps> = ({
  animalListData,
}) => {
  const { t } = useTranslation(['animal', 'common']);
  const { updateContent } = useDashboard();
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const animals = animalListData?.data || [];
  const totalCount = animalListData?.count || animals.length;
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  const currentAnimals = useMemo(() => {
    return animals.slice(startIndex, endIndex);
  }, [animals, startIndex, endIndex]);

  const handleRowClick = async (animal: AnimalData) => {
    // Usar animal.name em vez de animal.cattle_id.tag conforme solicitado
    const identifier = animal.name;
    
    if (!identifier || !animal.farm_id) {
      console.warn('[AnimalListView] Missing name or farm_id for animal:', animal);
      return;
    }

    const id = animal._key || animal._id || 'unknown';
    setLoadingId(id);

    try {
      const passportData = await getAnimalPassport(identifier, animal.farm_id);
      
      updateContent("animalPassport", {
        animalData: passportData,
        previousContentView: "animal-list"
      });
    } catch (error) {
      console.error("Error fetching animal passport:", error);
      // TODO: Add toast notification here
    } finally {
      setLoadingId(null);
    }
  };
  
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };
  
  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      {/* Summary Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
              <Box
                 sx={{ 
                   width: 32, 
                   height: 32,
                   backgroundColor: DESIGN_TOKENS.colors.organization.farm,
                   mask: `url(/vaca.png) no-repeat center`,
                   maskSize: 'contain',
                   WebkitMask: `url(/vaca.png) no-repeat center`,
                   WebkitMaskSize: 'contain'
                 }}
               />
              <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.organization.farm }}>
                {totalCount}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              {t('total_animals')}
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
              <Male sx={{ fontSize: 32, color: DESIGN_TOKENS.colors.primary[600] }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary[600] }}>
                {animals.filter(a => a.gender?.toLowerCase().includes('macho')).length}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              {t('males')}
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: "1 1 200px", minWidth: 200 }}>
          <CardContent sx={{ textAlign: "center", py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
              <Female sx={{ fontSize: 32, color: DESIGN_TOKENS.colors.secondary[600] }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.secondary[600] }}>
                {animals.filter(a => a.gender?.toLowerCase().includes('fêmea') || a.gender?.toLowerCase().includes('femea')).length}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              {t('females')}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 2 }}
        >
          {t('page_info', { 
            currentPage, 
            totalPages, 
            currentCount: currentAnimals.length, 
            totalCount 
          })}
        </Typography>
      </Box>

      {animals.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <img
              src="/vaca.png"
              alt={t('animal.cow_icon_alt')}
              style={{ 
                width: 64, 
                height: 64, 
                opacity: 0.6, 
                marginBottom: 16,
                filter: 'grayscale(100%) brightness(0.7)'
              }}
            />
            <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              {t('no_animals_found')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: DESIGN_TOKENS.colors.neutral[100] }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.animal')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.tag')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.breed')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.gender')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.age')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.weight')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.location')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('table_headers.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentAnimals.map((animal, index) => (
                  <TableRow
                    key={animal._key || animal._id || index}
                    hover
                    onClick={() => handleRowClick(animal)}
                    sx={{
                      cursor: 'pointer',
                      opacity: loadingId === (animal._key || animal._id) ? 0.7 : 1,
                      backgroundColor: loadingId === (animal._key || animal._id) ? DESIGN_TOKENS.colors.neutral[100] : 'inherit',
                      '&:hover': {
                        backgroundColor: DESIGN_TOKENS.colors.neutral[50],
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {loadingId === (animal._key || animal._id) ? (
                          <CircularProgress size={32} sx={{ color: DESIGN_TOKENS.colors.organization.farm }} />
                        ) : (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: DESIGN_TOKENS.colors.organization.farm,
                              fontSize: "0.875rem",
                            }}
                          >
                            {animal.name?.charAt(0) || "A"}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {animal.name || t('no_name')}
                      </Typography>
                          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                            {t('common:labels.id')}: {animal._key || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {animal.cattle_id?.tag || t('not_defined')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatBreed(animal.breed_code, t)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {getGenderIcon(animal.gender)}
                        <Typography variant="body2">
                          {animal.gender || t('not_defined')}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {calculateAge(animal.birth_date, t)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {animal.birth_weight_kg ? `${animal.birth_weight_kg} ${t('common:units.kg')}` : t('not_defined')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
                        <Typography variant="body2">
                          {animal.current_paddock_id || t('not_defined')}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={animal.current_status || t('not_defined')}
                        size="small"
                        color={statusChipColor(animal.current_status)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {totalCount > itemsPerPage && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBefore />}
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    size="small"
                  >
                    {t('previous')}
                  </Button>
                  
                  <Stack direction="row" spacing={1}>
                    {/* Primeira página */}
                    <Button
                      variant={currentPage === 1 ? "contained" : "outlined"}
                      onClick={() => handlePageClick(1)}
                      size="small"
                      sx={{
                        minWidth: 40,
                        bgcolor: currentPage === 1 ? DESIGN_TOKENS.colors.organization.farm : 'transparent',
                        '&:hover': {
                          bgcolor: currentPage === 1 
                            ? DESIGN_TOKENS.colors.organization.farm 
                            : DESIGN_TOKENS.colors.neutral[100]
                        }
                      }}
                    >
                      1
                    </Button>
                    
                    {/* {t('pagination.pages_near_current')} */}
                    {currentPage > 3 && (
                      <Typography variant="body2" sx={{ alignSelf: 'center', px: 1 }}>...</Typography>
                    )}
                    
                    {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                      let pageNum;
                      if (currentPage <= 3) {
                        pageNum = i + 2; // {t('pagination.show_pages_2_to_5')}
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i; // {t('pagination.show_last_4_pages')}
                      } else {
                        pageNum = currentPage - 1 + i; // {t('pagination.show_current_and_neighbors')}
                      }
                      
                      if (pageNum <= 1 || pageNum >= totalPages || pageNum > totalPages) return null;
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "contained" : "outlined"}
                          onClick={() => handlePageClick(pageNum)}
                          size="small"
                          sx={{
                            minWidth: 40,
                            bgcolor: currentPage === pageNum ? DESIGN_TOKENS.colors.organization.farm : 'transparent',
                            '&:hover': {
                              bgcolor: currentPage === pageNum 
                                ? DESIGN_TOKENS.colors.organization.farm 
                                : DESIGN_TOKENS.colors.neutral[100]
                            }
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    {/* {t('pagination.ellipsis_before_last')} */}
                    {currentPage < totalPages - 2 && totalPages > 5 && (
                      <Typography variant="body2" sx={{ alignSelf: 'center', px: 1 }}>...</Typography>
                    )}
                    
                    {/* {t('pagination.last_page')} */}
                    {totalPages > 1 && (
                      <Button
                        variant={currentPage === totalPages ? "contained" : "outlined"}
                        onClick={() => handlePageClick(totalPages)}
                        size="small"
                        sx={{
                          minWidth: 40,
                          bgcolor: currentPage === totalPages ? DESIGN_TOKENS.colors.organization.farm : 'transparent',
                          '&:hover': {
                            bgcolor: currentPage === totalPages 
                              ? DESIGN_TOKENS.colors.organization.farm 
                              : DESIGN_TOKENS.colors.neutral[100]
                          }
                        }}
                      >
                        {totalPages}
                      </Button>
                    )}
                  </Stack>
                  
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNext />}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    size="small"
                  >
                    {t('next')}
                  </Button>
                </Stack>
                
                <Typography
                  variant="caption"
                  sx={{ 
                    display: 'block', 
                    textAlign: 'center', 
                    mt: 2, 
                    color: DESIGN_TOKENS.colors.text.secondary 
                  }}
                >
                  {itemsPerPage} {t('items_per_page')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};